import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FaBuilding,
  FaCalendarAlt,
  FaCheck,
  FaCreditCard,
  FaEuroSign,
  FaFileInvoice,
  FaFileSignature,
  FaHistory,
  FaLink,
  FaPen,
  FaPlusCircle,
  FaSave,
  FaTimes,
} from "react-icons/fa";

import { FaRotateLeft } from "react-icons/fa6";

import ReactQuill from "react-quill";
import { toast } from "react-toastify";

import "react-quill/dist/quill.snow.css";

import {
  getAllAssociations,
} from "../../../../../services/associationsServices";

import {
  getSubscriptionApiError,
  getSubscriptionById,
  saveSubscription,
  updateSubscription,
} from "../../../../../services/subscriptionServices";

import {
  ONE_TIME_STATUS_OPTIONS,
  PAYMENT_PREVIEW_LABELS,
  PAYMENT_PREVIEW_STYLES,
  PRODUCT_OPTIONS,
  RENEWAL_FORM_OPTIONS,
  SUBSCRIPTION_STATUS_OPTIONS,
} from "./../data/subscriptionOptions";

import {
  buildInitialSubscriptionForm,
  buildSubscriptionPayload,
  computeAnnualEndDate,
  getPaymentPreview,
  getTodayISO,
  isSignedStatus,
  isUnsignedQuoteStatus,
  subscriptionToFormData,
  validateSubscriptionForm,
} from "../utils/subscriptionForm.utils";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
];

export default function SubscriptionFormModal({
  mode = "create",
  isOpen,
  onClose,
  onSuccess,
  refreshList,

  subscription = null,
  subscriptionId = null,

  renewedFromSubscriptionId = null,
}) {
  const isEditing = mode === "edit";

  const [formData, setFormData] = useState(() =>
    buildInitialSubscriptionForm({
      renewedFromSubscriptionId,
    }),
  );

  const [initialData, setInitialData] =
    useState(null);

  const [associations, setAssociations] =
    useState([]);

  const [
    loadingAssociations,
    setLoadingAssociations,
  ] = useState(false);

  const [loadingSubscription, setLoadingSubscription] =
    useState(false);

  const [saving, setSaving] = useState(false);

  const [errors, setErrors] = useState({});
  const [serverError, setServerError] =
    useState("");

  const [
    hasUserEditedEndDate,
    setHasUserEditedEndDate,
  ] = useState(false);

  /*
   * Initialisation à chaque ouverture.
   */
  useEffect(() => {
    if (!isOpen) return undefined;

    let isActive = true;

    const initializeModal = async () => {
      setErrors({});
      setServerError("");
      setHasUserEditedEndDate(false);

      setLoadingAssociations(true);

      try {
        const associationsResponse =
          await getAllAssociations();

        if (isActive) {
          setAssociations(
            Array.isArray(associationsResponse)
              ? [...associationsResponse].sort(
                  (left, right) =>
                    String(
                      left.nom_asso || "",
                    ).localeCompare(
                      String(
                        right.nom_asso || "",
                      ),
                      "fr",
                      {
                        sensitivity: "base",
                      },
                    ),
                )
              : [],
          );
        }
      } catch (error) {
        if (isActive) {
          toast.error(
            "Impossible de charger les associations.",
          );
        }
      } finally {
        if (isActive) {
          setLoadingAssociations(false);
        }
      }

      if (!isEditing) {
        const prepared =
          buildInitialSubscriptionForm({
            renewedFromSubscriptionId,
          });

        if (isActive) {
          setFormData(prepared);
          setInitialData(prepared);
        }

        return;
      }

      setLoadingSubscription(true);

      try {
        let sourceSubscription = subscription;

        if (
          !sourceSubscription &&
          subscriptionId
        ) {
          sourceSubscription =
            await getSubscriptionById(
              subscriptionId,
            );
        }

        if (!sourceSubscription) {
          throw new Error(
            "L’abonnement à modifier est introuvable.",
          );
        }

        const prepared =
          subscriptionToFormData(
            sourceSubscription,
          );

        if (isActive) {
          setFormData(prepared);
          setInitialData(prepared);
        }
      } catch (error) {
        if (isActive) {
          setServerError(
            getSubscriptionApiError(
              error,
              "Impossible de charger l’élément à modifier.",
            ),
          );
        }
      } finally {
        if (isActive) {
          setLoadingSubscription(false);
        }
      }
    };

    initializeModal();

    return () => {
      isActive = false;
    };
  }, [
    isOpen,
    isEditing,
    subscription?.id,
    subscriptionId,
    renewedFromSubscriptionId,
  ]);

  /*
   * Blocage du scroll et fermeture avec Échap.
   */
  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (
        event.key === "Escape" &&
        !saving
      ) {
        onClose?.();
      }
    };

    document.addEventListener(
      "keydown",
      handleEscape,
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      document.removeEventListener(
        "keydown",
        handleEscape,
      );
    };
  }, [isOpen, saving, onClose]);

  /*
   * Recalcul automatique uniquement pour un abonnement annuel.
   */
  useEffect(() => {
    if (!isOpen) return;
    if (hasUserEditedEndDate) return;
    if (formData.item_type !== "subscription") return;

    const suggestedEndDate =
      computeAnnualEndDate(
        formData.start_date,
      );

    setFormData((current) => {
      if (
        current.end_date === suggestedEndDate
      ) {
        return current;
      }

      return {
        ...current,
        end_date: suggestedEndDate,
      };
    });
  }, [
    isOpen,
    formData.item_type,
    formData.start_date,
    hasUserEditedEndDate,
  ]);

  const statusOptions = useMemo(() => {
    const options =
      formData.item_type === "one_time"
        ? ONE_TIME_STATUS_OPTIONS
        : SUBSCRIPTION_STATUS_OPTIONS;

    if (
      formData.status &&
      !options.includes(formData.status)
    ) {
      return [formData.status, ...options];
    }

    return options;
  }, [
    formData.item_type,
    formData.status,
  ]);

  const selectedAssociationName =
    useMemo(() => {
      const found = associations.find(
        (association) =>
          String(association.id) ===
          String(formData.asso_id),
      );

      return (
        found?.nom_asso ||
        "Aucune association sélectionnée"
      );
    }, [
      associations,
      formData.asso_id,
    ]);

  const paymentPreview = useMemo(
    () => getPaymentPreview(formData),
    [formData],
  );

  const hasChanges = useMemo(() => {
    if (!initialData) return false;

    return (
      JSON.stringify(formData) !==
      JSON.stringify(initialData)
    );
  }, [formData, initialData]);

  const clearFieldError = (fieldName) => {
    setErrors((current) => {
      if (!current[fieldName]) {
        return current;
      }

      const nextErrors = {
        ...current,
      };

      delete nextErrors[fieldName];

      return nextErrors;
    });

    setServerError("");
  };

  const handleStandardChange = (event) => {
    const { name, value } = event.target;

    clearFieldError(name);

    setFormData((current) => {
      const nextData = {
        ...current,
        [name]: value,
      };

      if (name === "end_date") {
        setHasUserEditedEndDate(true);
      }

      if (name === "status") {
        if (isUnsignedQuoteStatus(value)) {
          nextData.signed_at = "";
        } else if (
          isSignedStatus(value) &&
          !current.signed_at
        ) {
          nextData.signed_at = getTodayISO();
        }
      }

      if (name === "amount_paid") {
        const totalAmount = Number(
          current.total_amount || 0,
        );

        const amountPaid = Number(value || 0);

        if (
          totalAmount > 0 &&
          amountPaid >= totalAmount &&
          !current.paid_at
        ) {
          nextData.paid_at = getTodayISO();
        }

        if (amountPaid < totalAmount) {
          nextData.paid_at = "";
        }
      }

      if (name === "total_amount") {
        const totalAmount = Number(value || 0);
        const amountPaid = Number(
          current.amount_paid || 0,
        );

        if (amountPaid < totalAmount) {
          nextData.paid_at = "";
        }
      }

      if (
        name === "renewal_status" &&
        value === "quote_sent" &&
        !current.renewal_quote_sent_at
      ) {
        nextData.renewal_quote_sent_at =
          getTodayISO();
      }

      return nextData;
    });
  };

  const handleItemTypeChange = (itemType) => {
    setHasUserEditedEndDate(false);

    setFormData((current) => {
      if (itemType === "one_time") {
        return {
          ...current,
          item_type: "one_time",
          periodicity: "ponctuel",
          end_date: "",
          renewal_status: "not_applicable",
          renewal_quote_sent_at: "",
          last_reminder_at: "",
          status:
            ONE_TIME_STATUS_OPTIONS.includes(
              current.status,
            )
              ? current.status
              : "Devis en attente de signature",
        };
      }

      return {
        ...current,
        item_type: "subscription",
        periodicity: "annuel",
        end_date:
          current.end_date ||
          computeAnnualEndDate(
            current.start_date,
          ),
        renewal_status:
          current.renewal_status ===
          "not_applicable"
            ? "not_started"
            : current.renewal_status,
        status:
          SUBSCRIPTION_STATUS_OPTIONS.includes(
            current.status,
          )
            ? current.status
            : "Devis en attente de signature",
      };
    });
  };

  const handleMarkAsPaid = () => {
    const totalAmount = Number(
      formData.total_amount,
    );

    if (
      !Number.isFinite(totalAmount) ||
      totalAmount < 0
    ) {
      setErrors((current) => ({
        ...current,
        total_amount:
          "Renseigne d’abord le montant total.",
      }));

      return;
    }

    setFormData((current) => ({
      ...current,
      amount_paid: String(totalAmount),
      paid_at: getTodayISO(),
    }));
  };

  const handleClearPayment = () => {
    setFormData((current) => ({
      ...current,
      amount_paid: "0",
      paid_at: "",
    }));
  };

  const handleRecalculateEndDate = () => {
    setHasUserEditedEndDate(false);

    setFormData((current) => ({
      ...current,
      end_date: computeAnnualEndDate(
        current.start_date,
      ),
    }));
  };

  const handleReset = () => {
    if (!initialData || saving) return;

    setFormData(initialData);
    setErrors({});
    setServerError("");
    setHasUserEditedEndDate(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors =
      validateSubscriptionForm(formData);

    if (
      Object.keys(validationErrors).length
    ) {
      setErrors(validationErrors);

      toast.error(
        "Certains champs doivent être corrigés.",
      );

      return;
    }

    const payload =
      buildSubscriptionPayload(formData);

    setSaving(true);
    setErrors({});
    setServerError("");

    try {
      const savedSubscription = isEditing
        ? await updateSubscription(
            formData.id || subscriptionId,
            payload,
          )
        : await saveSubscription(payload);

      toast.success(
        isEditing
          ? "Élément modifié avec succès."
          : formData.item_type === "one_time"
            ? "Prestation ajoutée avec succès."
            : "Abonnement ajouté avec succès.",
      );

      refreshList?.();

      await Promise.resolve(
        onSuccess?.(savedSubscription),
      );

      onClose?.();
    } catch (error) {
      const apiErrors =
        error?.response?.data?.errors;

      if (
        apiErrors &&
        typeof apiErrors === "object"
      ) {
        setErrors(apiErrors);
      }

      const message =
        getSubscriptionApiError(
          error,
          isEditing
            ? "Impossible de modifier cet élément."
            : "Impossible d’ajouter cet élément.",
        );

      setServerError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const isLoading =
    loadingSubscription ||
    !initialData;

  const paymentStyle =
    PAYMENT_PREVIEW_STYLES[
      paymentPreview.paymentStatus
    ] ||
    PAYMENT_PREVIEW_STYLES.pending;

  return (
    <div className="fixed inset-0 z-[1200]">
      <div
        className="absolute inset-0 bg-slate-950/70 backdrop-blur-[3px]"
        onMouseDown={() => {
          if (!saving) {
            onClose?.();
          }
        }}
      />

      <div className="absolute inset-0 flex items-center justify-center p-2 sm:p-5">
        <form
          onSubmit={handleSubmit}
          className="relative flex max-h-[96vh] w-full max-w-7xl flex-col overflow-hidden rounded-[28px] border border-white/10 bg-white shadow-2xl dark:bg-slate-900"
        >
          <ModalHeader
            isEditing={isEditing}
            itemType={formData.item_type}
            saving={saving}
            onClose={onClose}
          />

          <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-950/60">
            {isLoading ? (
              <ModalLoading />
            ) : (
              <div className="grid gap-6 p-4 sm:p-6 xl:grid-cols-[minmax(0,1fr)_340px]">
                <div className="space-y-6">
                  {serverError && (
                    <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
                      {serverError}
                    </div>
                  )}

                  <FormSection
                    title="Type de vente"
                    subtitle="Choisis entre un abonnement renouvelable chaque année et une intervention facturée une seule fois."
                  >
                    <div className="grid gap-3 md:grid-cols-2">
                      <TypeCard
                        active={
                          formData.item_type ===
                          "subscription"
                        }
                        title="Abonnement annuel"
                        description="Site, application, nom de domaine ou service renouvelé chaque année."
                        icon={<FaHistory />}
                        onClick={() =>
                          handleItemTypeChange(
                            "subscription",
                          )
                        }
                      />

                      <TypeCard
                        active={
                          formData.item_type ===
                          "one_time"
                        }
                        title="Prestation ponctuelle"
                        description="Amélioration de code, ajout d’une fonctionnalité ou intervention unique."
                        icon={<FaPlusCircle />}
                        onClick={() =>
                          handleItemTypeChange(
                            "one_time",
                          )
                        }
                      />
                    </div>
                  </FormSection>

                  <FormSection
                    title="Client et vente"
                    subtitle="Identifie le client, le produit vendu et l’état commercial du dossier."
                  >
                    <div className="grid gap-4 md:grid-cols-2">
                      <Field
                        label="Association"
                        required
                        error={errors.asso_id}
                      >
                        <select
                          name="asso_id"
                          value={formData.asso_id}
                          onChange={
                            handleStandardChange
                          }
                          disabled={
                            loadingAssociations
                          }
                          className={getInputClass(
                            errors.asso_id,
                          )}
                        >
                          <option value="">
                            {loadingAssociations
                              ? "Chargement..."
                              : "Sélectionne une association"}
                          </option>

                          {associations.map(
                            (association) => (
                              <option
                                key={
                                  association.id
                                }
                                value={
                                  association.id
                                }
                              >
                                {
                                  association.nom_asso
                                }
                              </option>
                            ),
                          )}
                        </select>
                      </Field>

                      <Field
                        label={
                          formData.item_type ===
                          "one_time"
                            ? "Prestation"
                            : "Produit"
                        }
                        required
                        error={errors.product}
                      >
                        <input
                          list="subscription-product-options"
                          type="text"
                          name="product"
                          value={formData.product}
                          onChange={
                            handleStandardChange
                          }
                          className={getInputClass(
                            errors.product,
                          )}
                          placeholder={
                            formData.item_type ===
                            "one_time"
                              ? "Ex. Ajout des notifications push"
                              : "Ex. MonAppliClub Standard"
                          }
                        />

                        <datalist id="subscription-product-options">
                          {PRODUCT_OPTIONS.map(
                            (product) => (
                              <option
                                key={product}
                                value={product}
                              />
                            ),
                          )}
                        </datalist>
                      </Field>

                      <Field
                        label="Statut"
                        required
                        error={errors.status}
                      >
                        <select
                          name="status"
                          value={formData.status}
                          onChange={
                            handleStandardChange
                          }
                          className={getInputClass(
                            errors.status,
                          )}
                        >
                          {statusOptions.map(
                            (status) => (
                              <option
                                key={status}
                                value={status}
                              >
                                {status}
                              </option>
                            ),
                          )}
                        </select>
                      </Field>

                      <Field
                        label="Date de signature"
                        error={errors.signed_at}
                        help={
                          isUnsignedQuoteStatus(
                            formData.status,
                          )
                            ? "Le devis n’est pas encore signé."
                            : "Date utilisée pour le CA signé et les nouveaux clients."
                        }
                      >
                        <DateInput
                          name="signed_at"
                          value={
                            formData.signed_at
                          }
                          onChange={
                            handleStandardChange
                          }
                          disabled={isUnsignedQuoteStatus(
                            formData.status,
                          )}
                          error={
                            errors.signed_at
                          }
                        />
                      </Field>
                    </div>
                  </FormSection>

                  <FormSection
                    title={
                      formData.item_type ===
                      "one_time"
                        ? "Date de la prestation"
                        : "Période de l’abonnement"
                    }
                    subtitle={
                      formData.item_type ===
                      "one_time"
                        ? "Une prestation ponctuelle ne possède pas de date de renouvellement."
                        : "La date de fin déclenche les alertes de renouvellement."
                    }
                  >
                    <div className="grid gap-4 md:grid-cols-2">
                      <Field
                        label={
                          formData.item_type ===
                          "one_time"
                            ? "Date de la prestation"
                            : "Date de début"
                        }
                        required
                        error={errors.start_date}
                      >
                        <DateInput
                          name="start_date"
                          value={
                            formData.start_date
                          }
                          onChange={
                            handleStandardChange
                          }
                          error={
                            errors.start_date
                          }
                        />
                      </Field>

                      {formData.item_type ===
                        "subscription" && (
                        <Field
                          label="Date de fin"
                          required
                          error={errors.end_date}
                          action={
                            <button
                              type="button"
                              onClick={
                                handleRecalculateEndDate
                              }
                              className="text-xs font-bold text-indigo-600 hover:text-indigo-800 dark:text-indigo-300"
                            >
                              Recalculer
                            </button>
                          }
                        >
                          <DateInput
                            name="end_date"
                            value={
                              formData.end_date
                            }
                            onChange={
                              handleStandardChange
                            }
                            error={
                              errors.end_date
                            }
                          />
                        </Field>
                      )}
                    </div>
                  </FormSection>

                  <FormSection
                    title="Facturation et règlement"
                    subtitle="Ces données permettent d’identifier précisément les factures en retard."
                  >
                    <div className="grid gap-4 md:grid-cols-2">
                      <Field
                        label="Montant total"
                        required
                        error={
                          errors.total_amount
                        }
                      >
                        <MoneyInput
                          name="total_amount"
                          value={
                            formData.total_amount
                          }
                          onChange={
                            handleStandardChange
                          }
                          error={
                            errors.total_amount
                          }
                        />
                      </Field>

                      <Field
                        label="Montant réglé"
                        error={errors.amount_paid}
                        action={
                          <div className="flex gap-3">
                            <button
                              type="button"
                              onClick={
                                handleClearPayment
                              }
                              className="text-xs font-bold text-slate-500 hover:text-slate-800 dark:text-slate-400"
                            >
                              Remettre à zéro
                            </button>

                            <button
                              type="button"
                              onClick={
                                handleMarkAsPaid
                              }
                              className="text-xs font-bold text-emerald-600 hover:text-emerald-800 dark:text-emerald-300"
                            >
                              Tout régler
                            </button>
                          </div>
                        }
                      >
                        <MoneyInput
                          name="amount_paid"
                          value={
                            formData.amount_paid
                          }
                          onChange={
                            handleStandardChange
                          }
                          error={
                            errors.amount_paid
                          }
                        />
                      </Field>

                      <Field label="Date de facture">
                        <DateInput
                          name="invoice_date"
                          value={
                            formData.invoice_date
                          }
                          onChange={
                            handleStandardChange
                          }
                        />
                      </Field>

                      <Field
                        label="Date limite de paiement"
                        help="La ligne apparaîtra en retard après cette date si elle n’est pas totalement réglée."
                      >
                        <DateInput
                          name="payment_due_date"
                          value={
                            formData.payment_due_date
                          }
                          onChange={
                            handleStandardChange
                          }
                        />
                      </Field>

                      <Field label="Date du règlement complet">
                        <DateInput
                          name="paid_at"
                          value={formData.paid_at}
                          onChange={
                            handleStandardChange
                          }
                        />
                      </Field>

                      <div className="hidden md:block" />

                      <Field
                        label="Lien de la facture"
                        error={
                          errors.drive_invoice_link
                        }
                      >
                        <LinkInput
                          name="drive_invoice_link"
                          value={
                            formData.drive_invoice_link
                          }
                          onChange={
                            handleStandardChange
                          }
                          placeholder="https://drive.google.com/..."
                          error={
                            errors.drive_invoice_link
                          }
                        />
                      </Field>

                      <Field
                        label="Lien du devis"
                        error={
                          errors.drive_quote_link
                        }
                      >
                        <LinkInput
                          name="drive_quote_link"
                          value={
                            formData.drive_quote_link
                          }
                          onChange={
                            handleStandardChange
                          }
                          placeholder="https://... ou lien de signature"
                          error={
                            errors.drive_quote_link
                          }
                        />
                      </Field>
                    </div>

                    <div
                      className={`mt-5 rounded-2xl border p-4 ${paymentStyle}`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wider opacity-75">
                            Situation du paiement
                          </p>

                          <p className="mt-1 font-black">
                            {
                              PAYMENT_PREVIEW_LABELS[
                                paymentPreview
                                  .paymentStatus
                              ]
                            }
                          </p>
                        </div>

                        <p className="text-lg font-black">
                          {formatMoney(
                            paymentPreview.remainingAmount,
                          )}{" "}
                          restant
                        </p>
                      </div>

                      <div className="mt-3 h-2 overflow-hidden rounded-full bg-black/10 dark:bg-white/10">
                        <div
                          className="h-full rounded-full bg-current transition-all"
                          style={{
                            width: `${paymentPreview.percentage}%`,
                          }}
                        />
                      </div>
                    </div>
                  </FormSection>

                  {formData.item_type ===
                    "subscription" && (
                    <FormSection
                      title="Suivi du renouvellement"
                      subtitle="Pilote la préparation du prochain devis et les relances du client."
                    >
                      <div className="grid gap-4 md:grid-cols-3">
                        <Field label="État du renouvellement">
                          <select
                            name="renewal_status"
                            value={
                              formData.renewal_status
                            }
                            onChange={
                              handleStandardChange
                            }
                            className={getInputClass()}
                          >
                            {RENEWAL_FORM_OPTIONS.map(
                              (option) => (
                                <option
                                  key={
                                    option.value
                                  }
                                  value={
                                    option.value
                                  }
                                >
                                  {option.label}
                                </option>
                              ),
                            )}
                          </select>
                        </Field>

                        <Field label="Devis de renouvellement envoyé le">
                          <DateInput
                            name="renewal_quote_sent_at"
                            value={
                              formData.renewal_quote_sent_at
                            }
                            onChange={
                              handleStandardChange
                            }
                          />
                        </Field>

                        <Field label="Dernière relance">
                          <DateInput
                            name="last_reminder_at"
                            value={
                              formData.last_reminder_at
                            }
                            onChange={
                              handleStandardChange
                            }
                          />
                        </Field>
                      </div>
                    </FormSection>
                  )}

                  <FormSection
                    title="Détails complémentaires"
                    subtitle="Précise les options, le travail demandé ou les conditions particulières."
                  >
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700">
                      <ReactQuill
                        theme="snow"
                        value={formData.details}
                        onChange={(value) => {
                          setFormData(
                            (current) => ({
                              ...current,
                              details: value,
                            }),
                          );

                          setServerError("");
                        }}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Détails de l’abonnement ou de la prestation..."
                        className="subscription-quill"
                      />
                    </div>
                  </FormSection>
                </div>

                <SubscriptionSummary
                  formData={formData}
                  associationName={
                    selectedAssociationName
                  }
                  paymentPreview={
                    paymentPreview
                  }
                />
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 bg-white/95 px-4 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 sm:px-6">
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="button"
                onClick={handleReset}
                disabled={
                  saving ||
                  !hasChanges ||
                  isLoading
                }
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                <FaRotateLeft />

                Réinitialiser
              </button>

              <div className="flex flex-col-reverse gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 px-5 py-3 text-sm font-bold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  <FaTimes />

                  Annuler
                </button>

                <button
                  type="submit"
                  disabled={
                    saving ||
                    isLoading ||
                    (
                      isEditing &&
                      !hasChanges
                    )
                  }
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-5 py-3 text-sm font-black text-white shadow-lg transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FaSave />

                  {saving
                    ? "Enregistrement..."
                    : isEditing
                      ? "Enregistrer les modifications"
                      : formData.item_type ===
                          "one_time"
                        ? "Ajouter la prestation"
                        : "Ajouter l’abonnement"}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      <QuillStyles />
    </div>
  );
}

function ModalHeader({
  isEditing,
  itemType,
  saving,
  onClose,
}) {
  const isOneTime = itemType === "one_time";

  return (
    <header className="relative overflow-hidden border-b border-slate-800">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(99,102,241,0.30),transparent_30%),radial-gradient(circle_at_top_right,rgba(236,72,153,0.24),transparent_26%),linear-gradient(135deg,#0f172a_0%,#1e293b_45%,#312e81_100%)]" />

      <div className="relative flex items-start justify-between gap-4 px-5 py-5 text-white sm:px-7 sm:py-6">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-white/80">
            {isEditing ? (
              <FaPen />
            ) : (
              <FaPlusCircle />
            )}

            {isEditing
              ? "Modification"
              : "Nouvelle vente"}
          </div>

          <h2 className="mt-3 text-2xl font-black">
            {isEditing
              ? "Modifier la vente"
              : isOneTime
                ? "Ajouter une prestation ponctuelle"
                : "Ajouter un abonnement annuel"}
          </h2>

          <p className="mt-1 max-w-2xl text-sm text-white/70">
            Abonnements annuels et prestations
            ponctuelles sont gérés depuis le même
            formulaire.
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border border-white/15 bg-white/10 transition hover:bg-white/20 disabled:opacity-50"
          aria-label="Fermer"
        >
          <FaTimes />
        </button>
      </div>
    </header>
  );
}

function FormSection({
  title,
  subtitle,
  children,
}) {
  return (
    <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <h3 className="text-lg font-black text-slate-950 dark:text-white">
        {title}
      </h3>

      {subtitle && (
        <p className="mt-1 text-sm leading-5 text-slate-500 dark:text-slate-400">
          {subtitle}
        </p>
      )}

      <div className="mt-5">{children}</div>
    </section>
  );
}

function TypeCard({
  active,
  title,
  description,
  icon,
  onClick,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "relative rounded-2xl border p-4 text-left transition",
        active
          ? "border-indigo-500 bg-indigo-50 ring-4 ring-indigo-100 dark:bg-indigo-950/30 dark:ring-indigo-950"
          : "border-slate-200 bg-white hover:border-indigo-300 dark:border-slate-700 dark:bg-slate-800",
      ].join(" ")}
    >
      {active && (
        <span className="absolute right-3 top-3 grid h-6 w-6 place-items-center rounded-full bg-indigo-600 text-xs text-white">
          <FaCheck />
        </span>
      )}

      <div className="grid h-11 w-11 place-items-center rounded-2xl bg-slate-100 text-indigo-600 dark:bg-slate-900 dark:text-indigo-300">
        {icon}
      </div>

      <p className="mt-3 font-black text-slate-950 dark:text-white">
        {title}
      </p>

      <p className="mt-1 pr-4 text-sm leading-5 text-slate-500 dark:text-slate-400">
        {description}
      </p>
    </button>
  );
}

function Field({
  label,
  required = false,
  error,
  help,
  action,
  children,
}) {
  return (
    <div>
      <div className="mb-2 flex items-start justify-between gap-3">
        <label className="text-sm font-bold text-slate-700 dark:text-slate-200">
          {label}

          {required && (
            <span className="ml-1 text-red-500">
              *
            </span>
          )}
        </label>

        {action}
      </div>

      {children}

      {error ? (
        <p className="mt-1.5 text-xs font-semibold text-red-600 dark:text-red-300">
          {error}
        </p>
      ) : help ? (
        <p className="mt-1.5 text-xs leading-5 text-slate-400">
          {help}
        </p>
      ) : null}
    </div>
  );
}

function DateInput({
  name,
  value,
  onChange,
  error,
  disabled = false,
}) {
  return (
    <div className="relative">
      <FaCalendarAlt className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

      <input
        type="date"
        name={name}
        value={value || ""}
        onChange={onChange}
        disabled={disabled}
        className={`${getInputClass(
          error,
        )} pl-11 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 dark:disabled:bg-slate-900`}
      />
    </div>
  );
}

function MoneyInput({
  name,
  value,
  onChange,
  error,
}) {
  return (
    <div className="relative">
      <FaEuroSign className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

      <input
        type="number"
        name={name}
        value={value}
        onChange={onChange}
        min="0"
        step="0.01"
        className={`${getInputClass(
          error,
        )} pl-11`}
      />
    </div>
  );
}

function LinkInput({
  name,
  value,
  onChange,
  placeholder,
  error,
}) {
  return (
    <div className="relative">
      <FaLink className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

      <input
        type="url"
        name={name}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        className={`${getInputClass(
          error,
        )} pl-11`}
      />
    </div>
  );
}

function SubscriptionSummary({
  formData,
  associationName,
  paymentPreview,
}) {
  return (
    <aside className="space-y-5 xl:sticky xl:top-0 xl:self-start">
      <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-500">
          Résumé
        </p>

        <h3 className="mt-2 text-xl font-black text-slate-950 dark:text-white">
          {formData.item_type === "one_time"
            ? "Prestation ponctuelle"
            : "Abonnement annuel"}
        </h3>

        <div className="mt-5 space-y-3">
          <SummaryRow
            icon={<FaBuilding />}
            label="Client"
            value={associationName}
          />

          <SummaryRow
            icon={<FaFileInvoice />}
            label="Produit"
            value={
              formData.product ||
              "Non renseigné"
            }
          />

          <SummaryRow
            icon={<FaEuroSign />}
            label="Montant"
            value={formatMoney(
              paymentPreview.totalAmount,
            )}
          />

          <SummaryRow
            icon={<FaCreditCard />}
            label="Reste à payer"
            value={formatMoney(
              paymentPreview.remainingAmount,
            )}
          />

          <SummaryRow
            icon={<FaCalendarAlt />}
            label={
              formData.item_type ===
              "one_time"
                ? "Date"
                : "Période"
            }
            value={
              formData.item_type ===
              "one_time"
                ? formData.start_date || "—"
                : `${formData.start_date || "—"} → ${
                    formData.end_date || "—"
                  }`
            }
          />

          <SummaryRow
            icon={<FaFileSignature />}
            label="Signature"
            value={
              formData.signed_at ||
              "Non signée"
            }
          />
        </div>
      </section>

      <section className="rounded-[24px] border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-fuchsia-50 p-5 dark:border-indigo-900/50 dark:from-indigo-950/30 dark:via-slate-900 dark:to-fuchsia-950/20">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 dark:text-indigo-300">
          Point de vigilance
        </p>

        <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
          Le chiffre d’affaires du mois utilise la
          date de signature. Le retard de paiement
          utilise la date limite de règlement et le
          montant restant dû.
        </p>
      </section>
    </aside>
  );
}

function SummaryRow({
  icon,
  label,
  value,
}) {
  return (
    <div className="flex items-start gap-3 rounded-2xl bg-slate-50 p-3 dark:bg-slate-800/60">
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-white text-indigo-600 shadow-sm dark:bg-slate-900 dark:text-indigo-300">
        {icon}
      </div>

      <div className="min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
          {label}
        </p>

        <p className="mt-1 break-words text-sm font-bold text-slate-800 dark:text-slate-100">
          {value || "—"}
        </p>
      </div>
    </div>
  );
}

function ModalLoading() {
  return (
    <div className="grid gap-6 p-5 xl:grid-cols-[minmax(0,1fr)_340px]">
      <div className="space-y-5">
        {[1, 2, 3, 4].map((item) => (
          <div
            key={item}
            className="h-44 animate-pulse rounded-3xl bg-slate-200 dark:bg-slate-800"
          />
        ))}
      </div>

      <div className="h-96 animate-pulse rounded-3xl bg-slate-200 dark:bg-slate-800" />
    </div>
  );
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString(
    "fr-FR",
    {
      style: "currency",
      currency: "EUR",
    },
  );
}

function getInputClass(error = false) {
  return [
    "w-full rounded-2xl border bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 dark:bg-slate-800 dark:text-slate-100",
    error
      ? "border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-100 dark:border-red-700 dark:focus:ring-red-950"
      : "border-slate-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:focus:ring-indigo-900/40",
  ].join(" ");
}

function QuillStyles() {
  return (
    <style>{`
      .subscription-quill .ql-toolbar.ql-snow {
        border: 0;
        border-bottom: 1px solid rgb(226 232 240);
        padding: 12px;
        background: rgb(248 250 252);
      }

      .subscription-quill .ql-container.ql-snow {
        border: 0;
        min-height: 170px;
        font-size: 0.95rem;
      }

      .subscription-quill .ql-editor {
        min-height: 170px;
        color: #0f172a;
      }

      .dark .subscription-quill .ql-toolbar.ql-snow {
        background: rgb(15 23 42);
        border-bottom-color: rgb(51 65 85);
      }

      .dark .subscription-quill .ql-container.ql-snow {
        background: rgb(15 23 42);
      }

      .dark .subscription-quill .ql-editor {
        color: #e2e8f0;
      }

      .dark .subscription-quill .ql-stroke {
        stroke: #cbd5e1;
      }

      .dark .subscription-quill .ql-fill {
        fill: #cbd5e1;
      }

      .dark .subscription-quill .ql-picker {
        color: #cbd5e1;
      }
    `}</style>
  );
}